import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { enqueueSnackbar } from 'notistack';
import { DialogTitle } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import cs from 'classnames';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { saveUser } from '../../../store/user/userSlice';
import useDebounce from '../../../helpers/useDebounce';
import { useAppDispatch } from '../../../store/hooks';
import { ORGANIZATION } from '../../../enums/TYPES';
import styles from './AssignOrgModal.module.scss';
import { getAllOrganizations, getMyUserInfo, patchOrganization, patchOrganizationsRelations } from '../../../api';
import Search from '../../modules/Search';
import Modal from '../../modules/Modal';
import Avatar from '../../modules/Avatar';
import { useHotkeys } from 'react-hotkeys-hook';

type TAssignOrgModal = {
  open: boolean;
  switchModal: () => void;
  organization: ORGANIZATION;
  fetchAll?: () => Promise<unknown>;
};

const AssignOrgModal: React.FC<TAssignOrgModal> = ({ open, switchModal, organization, fetchAll }) => {
  const [loading, setLoading] = useState(false);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const onSubmit = async () => {
    setLoading(true);
    try {
      await patchOrganizationsRelations(organization.id, {
        newOrganizations: newOrgs.map((o) => +o.id),
        organizationsToRemove: orgsToRemove.map((o) => +o.id),
      });
      if (fetchAll) {
        await fetchAll();
      }
      const userJson = await getMyUserInfo();
      dispatch(saveUser(userJson.data));
      onClose();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.data?.message) {
        enqueueSnackbar(err?.response?.data?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    switchModal();
    setSearchedOrgs([]);
    setNewOrgs([]);
    setSearch('');
    setOldOrgSearch('');
    setOrgsToRemove([]);
  };

  const [search, setSearch] = useState('');
  const onSearch = (e: any) => {
    setSearch(e.target.value);
  };

  const [oldOrgSearch, setOldOrgSearch] = useState('');
  const onOldOrgSearch = (e: any) => {
    setOldOrgSearch(e.target.value);
  };

  const debouncedSearch = useDebounce(search, 1000);

  const [searchedOrgs, setSearchedOrgs] = useState<ORGANIZATION[]>([]);
  const [newOrgs, setNewOrgs] = useState<ORGANIZATION[]>([]);

  const relatedIds = useMemo(() => (organization?.related_organizations ?? []).map((o) => +o.id), [organization]);

  const onFetch = async () => {
    try {
      const type = organization.type === 'parent' ? 'child' : 'parent';
      setLoading(true);
      const res = await getAllOrganizations({
        page: 0,
        limit: 1000,
        search,
        selectedType: type,
        ignoreOrgIds: relatedIds,
      });

      if (res.data.documents) {
        const docs = [...res.data.documents].sort((a: ORGANIZATION, b: ORGANIZATION) => (a.name || '').localeCompare(b.name || ''));
        setSearchedOrgs(docs);
      }
    } catch (err: any) {
      console.log(err);
      if (err?.response?.data?.message) {
        enqueueSnackbar(err.response.data.message, {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      onFetch();
    }
  }, [debouncedSearch, open, relatedIds]);

  const onOrgAdd = (org: ORGANIZATION) => {
    if (newOrgs.find((o) => +o.id === +org.id)) {
      const arr = [...newOrgs].filter((o) => +o.id !== +org.id);
      setNewOrgs(arr);
    } else {
      setNewOrgs([...newOrgs, org]);
    }
  };

  const [orgsToRemove, setOrgsToRemove] = useState<ORGANIZATION[]>([]);
  const onOrgRemove = (org: ORGANIZATION) => {
    if (orgsToRemove.find((o) => +o.id === +org.id)) {
      const arr = [...orgsToRemove].filter((o) => +o.id !== +org.id);
      setOrgsToRemove(arr);
    } else {
      setOrgsToRemove([...orgsToRemove, org]);
    }
  };

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  const visibleSearchedOrgs = useMemo(() => searchedOrgs.filter((o) => !relatedIds.includes(+o.id)), [searchedOrgs, relatedIds]);

  return (
    <Modal open={open} onClose={loading ? console.log : onClose} loading={loading}>
      <div className={styles.container}>
        <div className={styles.modalTitle}>Assign {organization.type === 'parent' ? 'Child' : 'Parent'} Organisation</div>
        <div className={styles.modalTitleOrg}>{organization.name}</div>

        {newOrgs.length > 0 && (
          <Tooltip
            title={newOrgs.map((org) => (
              <div key={`${org.id}_tooltip`}>{org.name}</div>
            ))}
          >
            <div className={styles.tooltipSelected}> New Orgs Selected: {newOrgs.length} </div>
          </Tooltip>
        )}

        <div className={styles.innerContent}>
          <div>
            <div className={styles.subtitle}>
              {visibleSearchedOrgs.length > 0 && `Not assigned ${organization.type === 'parent' ? 'child' : 'parent'} organisations:`}
            </div>
            <Search className={styles.searchInput} onChange={onSearch} placeHolder="Search new organisation" />

            <div className={styles.searchedList}>
              {visibleSearchedOrgs.map((org) => (
                <div key={org.id} className={styles.orgObject}>
                  <Avatar avatar={org?.image_url} name={org.name} />
                  <div className={styles.orgInfo}>
                    <div className={styles.name}>{org.name}</div>
                    <div className={styles.location}>{org.location}</div>
                  </div>

                  <div className={styles.notifyCheckBox}>
                    <div className={styles.checkboxWrapper30}>
                      <span className={styles.checkbox}>
                        <input type="checkbox" onChange={() => onOrgAdd(org)} checked={!!newOrgs.find((o) => +o.id === +org.id)} />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {organization.related_organizations.length > 0 && (
            <div>
              <div className={styles.subtitle}>
                {organization.related_organizations.length > 0 &&
                  `Already assigned ${organization.type === 'parent' ? 'child' : 'parent'} organisations:`}
              </div>

              <Search className={styles.searchInput} onChange={onOldOrgSearch} placeHolder="Search already assigned organisation" />

              <div className={styles.oldOrgs}>
                {organization.related_organizations
                  .filter((org) => org.name.toLocaleLowerCase().includes(oldOrgSearch.toLocaleLowerCase()))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((org) => (
                    <div key={org.id} className={styles.orgObject}>
                      <Avatar avatar={org?.image_url} name={org.name} />
                      <div className={styles.orgInfo}>
                        <div className={styles.name}>{org.name}</div>
                        <div className={styles.location}>{org.location}</div>
                      </div>

                      <div className={styles.notifyCheckBox}>
                        <div className={styles.checkboxWrapper30}>
                          <span className={styles.checkbox}>
                            <input
                              type="checkbox"
                              onChange={() => onOrgRemove(org)}
                              checked={!orgsToRemove.find((o) => +o.id === +org.id)}
                            />
                            <svg>
                              <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                            </svg>
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                            <symbol id="checkbox-30" viewBox="0 0 22 22">
                              <path
                                fill="none"
                                stroke="currentColor"
                                d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                              />
                            </symbol>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.actionBtns}>
          <ContainedButton onClick={onSubmit} disabled={loading || (newOrgs.length === 0 && orgsToRemove.length === 0)}>
            <div ref={submitButtonRef}>Save changes</div>
          </ContainedButton>
          <OutlinedButton kind="default" onClick={onClose}>
            No, Cancel
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default AssignOrgModal;
